import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "@/components/layout/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useAuth } from "@/context/AuthContext";
import { homeForUser } from "@/components/ProtectedRoute";
import { formatApiError, api } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { isAdminPortal } from "@/lib/domain";

// Pick where to send the user post-login: explicit ?next= takes precedence
// over role-based default. Defensive: only allow same-origin relative paths.
const resolveNext = (nextParam, user) => {
  if (nextParam && typeof nextParam === "string" && nextParam.startsWith("/") && !nextParam.startsWith("//")) {
    return nextParam;
  }
  return homeForUser(user);
};

function PasswordForm() {
  const { loginPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState(null); // multi-account list
  const [selectedEmail, setSelectedEmail] = useState("");

  // Check for multiple accounts when mobile is entered
  const checkMultiAccount = async (val) => {
    setIdentifier(val);
    setAccounts(null);
    setSelectedEmail("");
    const digits = val.replace(/\D/g, "");
    if (digits.length === 10) {
      try {
        const { data } = await api.post("/auth/lookup", { mobile: digits });
        if (data.multiple && data.accounts?.length > 1) {
          setAccounts(data.accounts);
        }
      } catch { /* ignore lookup errors */ }
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const loginId = selectedEmail || identifier.trim();
    if (!loginId || !password) {
      toast.error("Enter your email/mobile and password");
      return;
    }
    setLoading(true);
    try {
      const user = await loginPassword(loginId, password);
      navigate(resolveNext(searchParams.get("next"), user), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="password-login-form">
      <div>
        <Label htmlFor="identifier">Email or mobile</Label>
        <Input
          id="identifier"
          data-testid="input-identifier"
          value={identifier}
          onChange={(e) => checkMultiAccount(e.target.value)}
          placeholder="you@company.in or 9876543210"
          className="mt-1"
        />
      </div>
      {/* Multi-account picker */}
      {accounts && accounts.length > 1 && (
        <div className="space-y-2" data-testid="account-picker">
          <Label className="text-xs text-amber-600">Multiple accounts found — select one:</Label>
          <div className="space-y-1.5">
            {accounts.map((acc) => (
              <button
                key={acc.email}
                type="button"
                onClick={() => { setSelectedEmail(acc.email); setIdentifier(acc.email); setAccounts(null); }}
                data-testid={`account-option-${acc.email}`}
                className={[
                  "w-full text-left px-3 py-2.5 rounded-lg border transition-colors text-sm",
                  selectedEmail === acc.email
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-accent/30",
                ].join(" ")}
              >
                <div className="font-medium">{acc.full_name || acc.email_masked}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                  <span>{acc.email_masked}</span>
                  <span className="uppercase text-[9px] font-mono bg-muted px-1 rounded">{acc.role}</span>
                  {acc.channel_name && <span className="text-primary font-medium">{acc.channel_name}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <Label htmlFor="pw">Password</Label>
        <div className="relative mt-1">
          <Input
            id="pw"
            type={showPw ? "text" : "password"}
            data-testid="input-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            data-testid="toggle-password-visibility"
            aria-label="Toggle password visibility"
            className="absolute inset-y-0 right-0 px-3 text-muted-foreground hover:text-foreground"
          >
            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <Button
        type="submit"
        size="lg"
        className="w-full tracking-wide"
        disabled={loading}
        data-testid="submit-password-login"
      >
        {loading ? "Signing in…" : "Log in"}
      </Button>
    </form>
  );
}

function OtpForm() {
  const { requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState("request"); // request | verify
  const [loading, setLoading] = useState(false);
  const [devOtp, setDevOtp] = useState(null);

  const requestHandler = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      toast.error("Enter a valid 10-digit Indian mobile");
      return;
    }
    setLoading(true);
    try {
      const res = await requestOtp(mobile);
      setDevOtp(res.dev_otp || null);
      setStage("verify");
      if (res.dev_otp) {
        toast.success("OTP generated. See dev hint below.");
      } else if (res.delivery_status === "success") {
        toast.success("OTP sent via SMS");
      } else {
        toast.warning(res.message || "OTP generated, but SMS delivery may have failed. Try again or contact support.");
      }
    } catch (err) {
      // New-user detection: backend 404 means "no account for this mobile".
      // Redirect into the signup flow with the mobile prefilled — preserves
      // any `?next=` deep-link too so the user lands where they intended after
      // completing signup.
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "";
      const isNoAccount =
        status === 404 ||
        /no account|sign up|not found/i.test(typeof detail === "string" ? detail : "");
      if (isNoAccount) {
        toast.info("New here? Let's set up your account.");
        const nextParam = searchParams.get("next");
        const qs = new URLSearchParams({ mobile });
        if (nextParam) qs.set("next", nextParam);
        navigate(`/signup?${qs.toString()}`, { replace: false });
        return;
      }
      toast.error(formatApiError(detail) || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyHandler = async (e) => {
    e.preventDefault();
    if (otp.length < 6) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const user = await verifyOtp(mobile, otp);
      navigate(resolveNext(searchParams.get("next"), user), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  if (stage === "request") {
    return (
      <form onSubmit={requestHandler} className="space-y-5" data-testid="otp-request-form">
        <div>
          <Label htmlFor="mobile">Registered mobile</Label>
          <Input
            id="mobile"
            inputMode="numeric"
            maxLength={10}
            data-testid="input-otp-mobile"
            value={mobile}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
            placeholder="9876543210"
            className="mt-1 font-mono tracking-widest"
          />
        </div>
        <Button
          type="submit"
          size="lg"
          className="w-full tracking-wide"
          disabled={loading}
          data-testid="submit-request-otp"
        >
          {loading ? "Sending OTP…" : "Send OTP"}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={verifyHandler} className="space-y-5" data-testid="otp-verify-form">
      <div>
        <div className="flex items-center justify-between">
          <Label>Enter OTP</Label>
          <button
            type="button"
            data-testid="change-mobile"
            className="text-xs text-primary hover:underline"
            onClick={() => {
              setStage("request");
              setOtp("");
              setDevOtp(null);
            }}
          >
            Change mobile
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-1 font-mono">
          Sent to +91 {mobile}
        </p>
        <div className="mt-3">
          <InputOTP
            maxLength={6}
            value={otp}
            onChange={setOtp}
            data-testid="input-otp"
          >
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
        {devOtp && (
          <div
            data-testid="dev-otp-hint"
            className="mt-3 rounded-md border border-dashed border-warning/60 bg-warning/5 px-3 py-2 text-xs"
          >
            <span className="overline text-warning">Dev hint</span>
            <div className="font-mono text-sm mt-1">OTP: {devOtp}</div>
            <p className="text-muted-foreground mt-1">
              SMS delivery is mocked in Phase 1. Real provider will be plugged in.
            </p>
          </div>
        )}
      </div>
      <Button
        type="submit"
        size="lg"
        className="w-full tracking-wide"
        disabled={loading}
        data-testid="submit-verify-otp"
      >
        {loading ? "Verifying…" : "Verify & continue"}
      </Button>
    </form>
  );
}

export default function Login() {
  const adminPortal = isAdminPortal();

  if (adminPortal) {
    // Admin portal: password-only login, no signup, no OTP
    return (
      <AuthLayout
        title="Admin Console"
        subtitle="Manage approvals, partners, integrations and platform settings."
        footer={
          <span className="text-xs text-muted-foreground">
            Partner or customer?{" "}
            <a href="https://flexpay.today/login" className="text-primary font-semibold hover:underline">
              Login at flexpay.today
            </a>
          </span>
        }
      >
        <PasswordForm />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Welcome back."
      subtitle="Access your FlexPay partner console. BBPS, payouts, beneficiaries and earnings — all in one place."
      footer={
        <span>
          New partner?{" "}
          <Link to="/signup" className="text-primary font-semibold hover:underline" data-testid="link-signup">
            Create an account
          </Link>
        </span>
      }
    >
      <Tabs defaultValue="otp" className="w-full" data-testid="login-tabs">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="otp" data-testid="tab-otp">Mobile OTP</TabsTrigger>
          <TabsTrigger value="password" data-testid="tab-password">Email / Password</TabsTrigger>
        </TabsList>
        <TabsContent value="otp" className="mt-6">
          <OtpForm />
        </TabsContent>
        <TabsContent value="password" className="mt-6">
          <PasswordForm />
        </TabsContent>
      </Tabs>
    </AuthLayout>
  );
}
